import { useEffect, useId, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import type { FileResponse } from "@/lib/api";
import type { AccountDocumentRow } from "@/lib/brokerAccounts";
import {
  documentOriginLabel,
  documentRelatedLabel,
  documentVersionLabel,
} from "@/lib/accountDocumentList";
import { saveFile } from "@/lib/downloadFile";
import { messageFor } from "@/lib/errors";
import { formatShortDate } from "@/lib/listFormat";
import { buttonClass } from "@/components/buttonStyles";

// BRK-S410 — the Documents tab's Preview. The bytes come through the same
// authed download the row's Download uses (an `<iframe src>` or `<object
// data>` pointed at the route would carry no bearer token and 401 into an
// empty frame), and render from an OBJECT URL that lives exactly as long as
// the dialog: revoked when it closes, never cached (NN #9). A PDF renders in
// an `<object>` (it takes a `type`, so a browser with no inline viewer shows
// the fallback rather than a blank box), an image inline, and anything else
// says "Preview not available" with Download. No vendor viewer.
//
// What it shows is decided by the CONTENT TYPE the server sent, never by the
// file's name: an extension is a claim, a content type is what was stored.

type Preview =
  | { status: "loading" }
  | { status: "failed"; message: string }
  | { status: "ready"; file: FileResponse; kind: "pdf" | "image"; url: string }
  | { status: "ready"; file: FileResponse; kind: "other" };

function kindOf(type: string): "pdf" | "image" | "other" {
  const t = type.toLowerCase();
  if (t === "application/pdf") return "pdf";
  if (t.startsWith("image/")) return "image";
  return "other";
}

/** A document as a caller outside the account list describes it (the
 *  submission's Documents tab, BRK-S424): its filename and the facts line
 *  under the title, already worded. */
export interface PreviewDocument {
  filename: string;
  meta: readonly string[];
}

export function DocumentPreviewDialog({
  row,
  fetchFile,
  onClose,
}: {
  row: AccountDocumentRow | PreviewDocument;
  /** The document's bytes. Read once per open (and per Try again). */
  fetchFile: () => Promise<FileResponse>;
  onClose: () => void;
}) {
  const titleId = useId();
  const [preview, setPreview] = useState<Preview>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  // The read is the one handed in when the dialog opened; a parent's re-render
  // with a fresh closure must not read the bytes again.
  const [read] = useState(() => fetchFile);

  useEffect(() => {
    let active = true;
    let url: string | null = null;
    setPreview({ status: "loading" });
    read()
      .then((file) => {
        if (!active) return;
        const kind = kindOf(file.blob.type);
        if (kind === "other") {
          setPreview({ status: "ready", file, kind });
          return;
        }
        url = URL.createObjectURL(file.blob);
        setPreview({ status: "ready", file, kind, url });
      })
      .catch((err: unknown) => {
        if (active) setPreview({ status: "failed", message: messageFor(err) });
      });
    return () => {
      active = false;
      if (url !== null) URL.revokeObjectURL(url);
    };
  }, [read, attempt]);

  const meta =
    "meta" in row
      ? row.meta
      : [
          row.documentNumber,
          documentVersionLabel(row),
          row.typeLabel,
          documentRelatedLabel(row),
          `Added ${formatShortDate(row.createdAt)}`,
          documentOriginLabel(row.source),
        ].filter((part): part is string => Boolean(part));

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId} className="[overflow-wrap:anywhere]">
        {row.filename}
      </DialogTitle>
      <DialogContent>
        <p className="mb-3 text-sm text-brk-dim [overflow-wrap:anywhere]">
          {meta.join(" · ")}
        </p>
        {preview.status === "loading" && (
          <p role="status" className="text-sm text-brk-dim">
            Loading the preview…
          </p>
        )}
        {preview.status === "failed" && (
          <div className="flex flex-col items-start gap-2">
            <p role="alert" className="text-sm text-brk-danger">
              {preview.message}
            </p>
            <button
              type="button"
              onClick={() => setAttempt((n) => n + 1)}
              className={buttonClass({ variant: "secondary", size: "sm" })}
            >
              Try again
            </button>
          </div>
        )}
        {preview.status === "ready" && preview.kind === "pdf" && (
          <div className="min-w-0 overflow-hidden rounded-xl border border-brk-border bg-brk-panel">
            <object
              data={preview.url}
              type="application/pdf"
              aria-label={`Preview of ${row.filename}`}
              className="h-[70vh] w-full"
            >
              <p className="p-5 text-sm text-brk-dim">
                Your browser can’t show this PDF inline. Download it instead.
              </p>
            </object>
          </div>
        )}
        {preview.status === "ready" && preview.kind === "image" && (
          <img
            src={preview.url}
            alt={row.filename}
            className="mx-auto max-h-[70vh] max-w-full rounded-xl border border-brk-border"
          />
        )}
        {preview.status === "ready" && preview.kind === "other" && (
          <div className="rounded-xl border border-dashed border-brk-border bg-brk-panel px-4 py-10 text-center">
            <p className="text-sm font-medium text-brk-text">
              Preview not available
            </p>
            <p className="mt-1 text-sm text-brk-dim">
              This kind of file can’t be shown here. Download it to open it.
            </p>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {preview.status === "ready" && (
          <button
            type="button"
            // The bytes already held: saving them reads nothing again.
            onClick={() => saveFile(preview.file, row.filename)}
            aria-label={`Download ${row.filename}`}
            className={buttonClass({ variant: "secondary" })}
          >
            Download
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className={buttonClass({ variant: "primary" })}
        >
          Close
        </button>
      </DialogActions>
    </Dialog>
  );
}
